import styled from '@emotion/styled';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { challengesQueries } from '@/apis/challenges/challenges.query';
import { Button } from '@/components/Button';
import { Layout } from '@/components/Layout';
import Pagination from '@/components/Pagination';
import {
  ChallengeParticipantsTableBody,
  ChallengeParticipantsTableBodyError,
  ChallengeParticipantsTableBodyLoading,
} from '@/pages/challenges/ChallengeParticipantsTableBody';

const PARTICIPANTS_PAGE_SIZE = 10;
const PARTICIPANT_TEAM_FILTER_OPTIONS = ['ALL', 'YES', 'NO'] as const;
const PARTICIPANT_SURVIVAL_FILTER_OPTIONS = [
  'ALL',
  'SURVIVED',
  'FAILED',
] as const;
const getParticipantSearchParams = (
  page: number,
  teamId: string,
  hasTeam: string,
  survival: string,
) => ({
  page,
  teamId: teamId.trim() ? teamId : undefined,
  hasTeam: hasTeam !== 'ALL' ? hasTeam : undefined,
  survival: survival !== 'ALL' ? survival : undefined,
});

export const Route = createFileRoute('/_admin/challenges/$challengeId/teams')({
  validateSearch: (search: Record<string, unknown>) => {
    const rawHasTeam =
      typeof search.hasTeam === 'string' ? search.hasTeam : 'ALL';
    const rawSurvival =
      typeof search.survival === 'string' ? search.survival : 'ALL';

    return {
      page: Math.max(Number(search.page ?? 0) || 0, 0),
      teamId:
        typeof search.teamId === 'string' && search.teamId.trim()
          ? search.teamId
          : undefined,
      hasTeam: PARTICIPANT_TEAM_FILTER_OPTIONS.includes(
        rawHasTeam as (typeof PARTICIPANT_TEAM_FILTER_OPTIONS)[number],
      )
        ? rawHasTeam === 'ALL'
          ? undefined
          : rawHasTeam
        : undefined,
      survival: PARTICIPANT_SURVIVAL_FILTER_OPTIONS.includes(
        rawSurvival as (typeof PARTICIPANT_SURVIVAL_FILTER_OPTIONS)[number],
      )
        ? rawSurvival === 'ALL'
          ? undefined
          : rawSurvival
        : undefined,
    };
  },
  component: ChallengeTeamsPage,
});

function ChallengeTeamsPage() {
  return (
    <Layout title="챌린지 팀 관리">
      <ErrorBoundary fallback={<div>에러가 발생했습니다.</div>}>
        <Suspense fallback={<div>로딩 중...</div>}>
          <ChallengeTeamsContent />
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
}

function ChallengeTeamsContent() {
  const { challengeId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const [participantsTotal, setParticipantsTotal] = useState(0);
  const [participantsTotalPages, setParticipantsTotalPages] = useState(0);
  const [maxTeamSizeInput, setMaxTeamSizeInput] = useState('15');
  const [createCountInput, setCreateCountInput] = useState('1');

  const id = Number(challengeId);

  const { data: challenge } = useSuspenseQuery(challengesQueries.detail(id));
  const { data: teams } = useSuspenseQuery(challengesQueries.teams(id));

  const challengeTeamId = useMemo(() => {
    const trimmed = (search.teamId ?? '').trim();
    if (!trimmed) {
      return undefined;
    }

    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [search.teamId]);

  const hasTeam = useMemo(() => {
    if (!search.hasTeam) {
      return undefined;
    }

    return search.hasTeam === 'YES';
  }, [search.hasTeam]);

  const isSurvived = useMemo(() => {
    if (!search.survival) {
      return undefined;
    }

    return search.survival === 'SURVIVED';
  }, [search.survival]);

  const maxTeamSize = useMemo(() => {
    const trimmed = maxTeamSizeInput.trim();
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [maxTeamSizeInput]);

  const createCount = useMemo(() => {
    const trimmed = createCountInput.trim();
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [createCountInput]);

  const handleParticipantsDataLoaded = useCallback(
    (totalElements: number, totalPages: number) => {
      setParticipantsTotal(totalElements);
      setParticipantsTotalPages(totalPages);
    },
    [],
  );

  const handleParticipantsPageChange = (page: number) => {
    if (page < 0 || page === search.page || page >= participantsTotalPages) {
      return;
    }

    navigate({
      search: getParticipantSearchParams(
        page,
        search.teamId ?? '',
        search.hasTeam ?? 'ALL',
        search.survival ?? 'ALL',
      ),
    });
  };

  const handleTeamIdChange = (value: string) => {
    navigate({
      search: getParticipantSearchParams(
        0,
        value,
        search.hasTeam ?? 'ALL',
        search.survival ?? 'ALL',
      ),
    });
  };

  const handleHasTeamChange = (value: 'ALL' | 'YES' | 'NO') => {
    navigate({
      search: getParticipantSearchParams(
        0,
        search.teamId ?? '',
        value,
        search.survival ?? 'ALL',
      ),
    });
  };

  const handleSurvivalChange = (value: 'ALL' | 'SURVIVED' | 'FAILED') => {
    navigate({
      search: getParticipantSearchParams(
        0,
        search.teamId ?? '',
        search.hasTeam ?? 'ALL',
        value,
      ),
    });
  };

  const { mutate: assignTeams, isPending: isAssigningTeams } = useMutation({
    ...challengesQueries.mutation.assignTeams(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges', 'teams', id] });
      queryClient.invalidateQueries({
        queryKey: ['challenges', 'participants', id],
      });
      alert('팀 자동 배정이 완료되었습니다.');
    },
    onError: () => {
      alert('팀 자동 배정에 실패했습니다.');
    },
  });

  const { mutate: createTeams, isPending: isCreatingTeams } = useMutation({
    ...challengesQueries.mutation.createTeams(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges', 'teams', id] });
      alert('팀 생성이 완료되었습니다.');
      setCreateCountInput('1');
    },
    onError: () => {
      alert('팀 생성에 실패했습니다.');
    },
  });

  const { mutate: deleteTeam, isPending: isDeletingTeam } = useMutation({
    ...challengesQueries.mutation.deleteTeam(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges', 'teams', id] });
      queryClient.invalidateQueries({
        queryKey: ['challenges', 'participants', id],
      });
      alert('팀 삭제가 완료되었습니다.');
    },
    onError: () => {
      alert('팀 삭제에 실패했습니다.');
    },
  });

  const handleAssignTeams = () => {
    if (maxTeamSize <= 0) {
      alert('팀 최대 인원은 1명 이상 입력해주세요.');
      return;
    }

    if (
      confirm(
        '이미 팀이 배정된 경우 기존 배정이 초기화되고 재배정됩니다. 진행할까요?',
      )
    ) {
      assignTeams({ challengeId: id, maxTeamSize });
    }
  };

  const handleCreateTeams = () => {
    if (createCount <= 0) {
      alert('생성할 팀 개수는 1 이상 입력해주세요.');
      return;
    }

    createTeams({ challengeId: id, count: createCount });
  };

  const handleDeleteTeam = (teamId: number) => {
    if (confirm('해당 팀을 삭제하시겠습니까?')) {
      deleteTeam({ challengeId: id, teamId });
    }
  };

  const handleGoToDetail = () => {
    navigate({
      to: '/challenges/$challengeId',
      params: { challengeId },
      search: getParticipantSearchParams(
        search.page,
        search.teamId ?? '',
        search.hasTeam ?? 'ALL',
        search.survival ?? 'ALL',
      ),
    });
  };

  return (
    <Container>
      <Header>
        <TitleGroup>
          <Title>{challenge?.name ?? '팀 관리'}</Title>
          {challenge && (
            <MetaText>
              <GenerationBadge>{challenge.generation}기</GenerationBadge>
              <span>
                {challenge.startDate} ~ {challenge.endDate}
              </span>
            </MetaText>
          )}
        </TitleGroup>
        <Button variant="secondary" onClick={handleGoToDetail}>
          챌린지 상세
        </Button>
      </Header>

      <Section>
        <SectionHeader>
          <SectionTitle>팀 자동 배정</SectionTitle>
        </SectionHeader>
        <Notice>팀이 자동으로 생성되며 참여자는 랜덤으로 배정됩니다.</Notice>
        <ActionRow>
          <Label htmlFor="max-team-size">팀 최대 인원</Label>
          <Input
            id="max-team-size"
            type="number"
            min="1"
            value={maxTeamSizeInput}
            onChange={(event) => setMaxTeamSizeInput(event.target.value)}
          />
          <Button onClick={handleAssignTeams} disabled={isAssigningTeams}>
            {isAssigningTeams ? '배정 중...' : '자동 배정'}
          </Button>
        </ActionRow>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>팀 생성/삭제</SectionTitle>
        </SectionHeader>
        <Notice>
          입력한 개수만큼 팀이 자동으로 생성됩니다. 팀 자동 배정을 이미
          실행했다면 보통 추가 생성은 필요하지 않습니다. 팀 삭제 시 해당 팀에
          소속된 참여자의 팀은 미배정 상태가 됩니다.
        </Notice>
        <ActionRow>
          <Label htmlFor="create-team-count">생성 개수</Label>
          <Input
            id="create-team-count"
            type="number"
            min="1"
            value={createCountInput}
            onChange={(event) => setCreateCountInput(event.target.value)}
          />
          <Button
            variant="secondary"
            onClick={handleCreateTeams}
            disabled={isCreatingTeams}
          >
            {isCreatingTeams ? '생성 중...' : '팀 생성'}
          </Button>
        </ActionRow>
        <TeamsTable>
          <colgroup>
            <col style={{ width: '35%' }} />
            <col style={{ width: '35%' }} />
            <col style={{ width: '30%' }} />
          </colgroup>
          <Thead>
            <Tr>
              <Th>팀 ID</Th>
              <Th>진행률</Th>
              <Th>관리</Th>
            </Tr>
          </Thead>
          <Tbody>
            {teams?.length ? (
              teams.map((team) => (
                <Tr key={team.id}>
                  <Td>{team.id}</Td>
                  <Td>{team.progress}%</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteTeam(team.id)}
                      disabled={isDeletingTeam}
                    >
                      삭제
                    </Button>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={3}>
                  <EmptyState>생성된 팀이 없습니다.</EmptyState>
                </Td>
              </Tr>
            )}
          </Tbody>
        </TeamsTable>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>참여자 팀 관리</SectionTitle>
        </SectionHeader>
        <Filters>
          <FilterGroup>
            <FilterLabel htmlFor="participant-team-id">팀 ID</FilterLabel>
            <FilterSelect
              id="participant-team-id"
              value={search.teamId ?? ''}
              onChange={(event) => handleTeamIdChange(event.target.value)}
            >
              <option value="">전체</option>
              {teams?.map((team) => (
                <option key={team.id} value={team.id.toString()}>
                  {team.id}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel htmlFor="participant-has-team">팀 매칭</FilterLabel>
            <FilterSelect
              id="participant-has-team"
              value={search.hasTeam ?? 'ALL'}
              onChange={(event) =>
                handleHasTeamChange(event.target.value as 'ALL' | 'YES' | 'NO')
              }
            >
              <option value="ALL">전체</option>
              <option value="YES">매칭됨</option>
              <option value="NO">미매칭</option>
            </FilterSelect>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel htmlFor="participant-survival">상태</FilterLabel>
            <FilterSelect
              id="participant-survival"
              value={search.survival ?? 'ALL'}
              onChange={(event) =>
                handleSurvivalChange(
                  event.target.value as 'ALL' | 'SURVIVED' | 'FAILED',
                )
              }
            >
              <option value="ALL">전체</option>
              <option value="SURVIVED">생존</option>
              <option value="FAILED">탈락</option>
            </FilterSelect>
          </FilterGroup>
        </Filters>
        <ParticipantsTable>
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <Thead>
            <Tr>
              <Th>닉네임</Th>
              <Th>팀 ID</Th>
              <Th>완료 일수</Th>
              <Th>쉴드</Th>
              <Th>상태</Th>
              <Th>관리</Th>
            </Tr>
          </Thead>
          <ErrorBoundary
            fallback={<ChallengeParticipantsTableBodyError colSpan={6} />}
          >
            <Suspense
              fallback={<ChallengeParticipantsTableBodyLoading colSpan={6} />}
            >
              <ChallengeParticipantsTableBody
                challengeId={id}
                currentPage={search.page}
                pageSize={PARTICIPANTS_PAGE_SIZE}
                challengeTeamId={challengeTeamId}
                hasTeam={hasTeam}
                isSurvived={isSurvived}
                onDataLoaded={handleParticipantsDataLoaded}
                editable
              />
            </Suspense>
          </ErrorBoundary>
        </ParticipantsTable>

        {participantsTotal > 0 && participantsTotalPages > 0 && (
          <Pagination
            totalCount={participantsTotal}
            totalPages={participantsTotalPages}
            currentPage={search.page}
            onPageChange={handleParticipantsPageChange}
            countUnitLabel="명"
          />
        )}
      </Section>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  flex-direction: column;
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  justify-content: space-between;

  background-color: ${({ theme }) => theme.colors.white};
`;

const Title = styled.h2`
  margin: 0;

  color: ${({ theme }) => theme.colors.gray900};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize['2xl']};
`;

const TitleGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  align-items: center;
`;

const MetaText = styled.span`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;

  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const GenerationBadge = styled.span`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};

  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1;
`;

const Section = styled.section`
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  background-color: ${({ theme }) => theme.colors.white};
`;

const SectionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.h3`
  margin: 0;

  color: ${({ theme }) => theme.colors.gray900};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.lg};
`;

const Notice = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};

  color: ${({ theme }) => theme.colors.gray600};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.6;
`;

const ActionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.gray900};
  font-size: ${({ theme }) => theme.fontSize.base};
`;

const Input = styled.input`
  max-width: 160px;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.4;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TeamsTable = styled.table`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.md};

  border-collapse: collapse;
  table-layout: fixed;
`;

const ParticipantsTable = styled.table`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.md};

  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background-color: ${({ theme }) => theme.colors.gray50};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray50};
  }
`;

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};

  color: ${({ theme }) => theme.colors.gray700};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: left;

  &:nth-of-type(2),
  &:nth-of-type(3),
  &:nth-of-type(4),
  &:nth-of-type(5),
  &:nth-of-type(6) {
    text-align: center;
  }
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};

  color: ${({ theme }) => theme.colors.gray700};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.lg} 0;

  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
`;

const Filters = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const FilterLabel = styled.label`
  color: ${({ theme }) => theme.colors.gray600};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const FilterSelect = styled.select`
  min-width: 120px;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.gray700};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;
